import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services';

const normalizeReturnUrl = (returnUrl: string | undefined): string => {
  const path = (returnUrl ?? '/').trim();
  const pathWithoutQuery = path.split('?')[0] || '/';

  if (!pathWithoutQuery.startsWith('/') || pathWithoutQuery.startsWith('/signup')) {
    return '/';
  }

  return pathWithoutQuery;
};

export const AuthGuard: CanActivateFn = async (_next, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const callbackParams = new URLSearchParams(window.location.search);

  if (callbackParams.has('code') && await authService.isAuthenticated()) {
    if (!(await authService.isEmailVerified())) {
      const returnUrl = encodeURIComponent(state.url || '/');
      return router.parseUrl(`/signup?verifyEmail=1&returnUrl=${returnUrl}`);
    }

    if (!(await authService.isPhoneVerified())) {
      const returnUrl = encodeURIComponent(state.url || '/');
      return router.parseUrl(`/signup?verifyPhone=1&returnUrl=${returnUrl}`);
    }

    const returnUrl = normalizeReturnUrl(state.url);
    if (state.url !== returnUrl) {
      return router.parseUrl(returnUrl);
    }

    return true;
  }

  const hostedUiResult = await authService.handleHostedUiCallback(callbackParams);
  if (hostedUiResult.status === 'authenticated') {
    const callbackReturnUrl = normalizeReturnUrl(hostedUiResult.returnUrl ?? state.url);
    if (!(await authService.isEmailVerified())) {
      const returnUrl = encodeURIComponent(callbackReturnUrl);
      return router.parseUrl(`/signup?verifyEmail=1&returnUrl=${returnUrl}`);
    }

    if (!(await authService.isPhoneVerified())) {
      const returnUrl = encodeURIComponent(callbackReturnUrl);
      return router.parseUrl(`/signup?verifyPhone=1&returnUrl=${returnUrl}`);
    }

    const returnUrl = normalizeReturnUrl(hostedUiResult.returnUrl ?? state.url);
    if (state.url !== returnUrl) {
      return router.parseUrl(returnUrl);
    }

    return true;
  }

  if (hostedUiResult.status === 'error') {
    const isCallbackRecoveryError =
      hostedUiResult.errorMessage.includes('sign-in request session is missing')
      || hostedUiResult.errorMessage.includes('authentication response state is invalid');

    if (isCallbackRecoveryError) {
      const fallbackUrl = '/signup';
      const encodedError = encodeURIComponent('Unable to complete sign-in. Please try again.');
      return router.parseUrl(`${fallbackUrl}?authError=${encodedError}`);
    }

    const fallbackUrl = '/signup';
    const encodedError = encodeURIComponent(hostedUiResult.errorMessage);
    return router.parseUrl(`${fallbackUrl}?authError=${encodedError}`);
  }

  if (await authService.isAuthenticated()) {
    if (!(await authService.isEmailVerified())) {
      const returnUrl = encodeURIComponent(state.url || '/');
      return router.parseUrl(`/signup?verifyEmail=1&returnUrl=${returnUrl}`);
    }

    if (!(await authService.isPhoneVerified())) {
      const returnUrl = encodeURIComponent(state.url || '/');
      return router.parseUrl(`/signup?verifyPhone=1&returnUrl=${returnUrl}`);
    }

    return true;
  }

  authService.login(normalizeReturnUrl(state.url));
  return false;
};
